import { Button as BaseButton } from '@base-ui/react/button';
import type { ComponentProps } from 'react';
import { cx } from '../lib/styled.tsx';

// catalog-guard: allow brand_color_roles — «primary», «secondary» and
// «accent» are this button's variants. They collide with the palette's role
// names and mean something else entirely: a merchant's accent colour has
// nothing to do with which button is the loud one.
export type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

/*
 * Everything that reads as a button is a pill. Disabled is a flat pale fill
 * rather than the usual opacity, because a translucent gradient over a tinted
 * surface picks up the tint.
 */
const base =
  // 600, measured. 500 is what this had, and across a row of buttons it is
  // the difference between a toolbar and a suggestion.
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap ' +
  'transition-[background-color,background-image,color,box-shadow,border-color] duration-150 ease-out-quint ' +
  'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-none ' +
  'disabled:bg-border-subtle disabled:text-ink-faint disabled:shadow-none ' +
  'data-disabled:cursor-not-allowed data-disabled:border-transparent data-disabled:bg-none ' +
  'data-disabled:bg-border-subtle data-disabled:text-ink-faint data-disabled:shadow-none';

const variants: Record<ButtonVariant, string> = {
  /* The near-black gradient. `text-surface` is the page colour, so it inverts with the theme. */
  primary:
    'bg-[image:var(--gradient-primary)] text-surface ' +
    'hover:not-disabled:bg-[image:var(--gradient-primary-hover)]',
  /*
   * Coral is a fill, never text — and what sits on it is `brand-ink`, which
   * stays dark in both themes because the fill does.
   */
  /*
   * No shadow on any of these. Measured: the product's buttons are flat, and
   * `--shadow-button` belongs to the things that are *not* buttons — the filter
   * chip, the segmented control's selected half.
   */
  accent: 'bg-brand-400 text-brand-ink hover:not-disabled:bg-brand-300',
  /*
   * Page-coloured with a 2px edge, not white with a hairline: it sits *in* a
   * white panel as often as on the page, and a white fill there disappears.
   */
  secondary:
    'border-2 border-border-subtle bg-surface text-ink ' + 'hover:not-disabled:border-brand-300',
  /* A ghost button has no fill to pale out, so it does not grow one when disabled. */
  ghost:
    'text-ink-muted hover:not-disabled:bg-surface-muted hover:not-disabled:text-ink ' +
    'disabled:bg-transparent data-disabled:bg-transparent',
  danger: 'bg-danger text-danger-soft hover:not-disabled:brightness-95',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 gap-1.5 px-4 text-sm',
  md: 'h-10 px-4 text-bodysm' /* 40x, padding 8/16 — measured */,
  lg: 'h-11 px-6 text-md',
  icon: 'size-10 p-0',
};

export interface ButtonProps extends ComponentProps<typeof BaseButton> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

/**
 * Wraps Base UI's Button so the `render` prop, `focusableWhenDisabled` and the
 * data-* state attributes stay available while styling is fixed by the tokens.
 */
export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonProps) {
  return (
    <BaseButton
      className={cx(base, variants[variant], sizes[size], className)}
      // So a layout can tell a square from a label without inspecting children.
      // `Actions` uses it: narrow, a labelled button takes the width and an
      // icon stays square beside it, which is what the product does.
      data-icon-button={size === 'icon' ? '' : undefined}
      {...props}
    />
  );
}
